import React, { useContext, useState } from 'react';
import { HiOutlineTrash, HiOutlinePlus } from 'react-icons/hi2';

import StepRow, { hasMore } from './step-row';
import AddStepMenu from './add-step-menu';
import BranchCondition from './branch-condition';
import StepSettings from './step-settings';
import { PlaybookContext } from '../context/PlaybookContext';
import { STEP_KIND, branchMode } from '../model/playbook-model';

/**
 * One numbered section - "1. Order status" - and the steps inside it.
 *
 * Reordering uses native HTML5 drag events, the same approach the choice-rule
 * editor uses (settings/choice/choice-settings.js); this repo has no drag-drop
 * library and adding one for a single list is not worth the dependency.
 */

export default function Section({ section, index, lookups, slotOptions, onCreateVariable }) {
    const {
        playbook, errorsByStep, selectedStepId, setSelectedStepId,
        addStep, removeStep, moveStep, renameSection, removeSection,
        addBranch, updateBranch, removeBranch, updateStepConfig, updateStepKind,
    } = useContext(PlaybookContext);

    const [dragId, setDragId] = useState(null);
    const [dragOverId, setDragOverId] = useState(null);
    const [editingTitle, setEditingTitle] = useState(false);

    const dropHandlers = (branchOf, stepId) => ({
        draggable: true,
        dragging: dragId === stepId,
        onDragStart: () => setDragId(stepId),
        onDragOver: e => { e.preventDefault(); setDragOverId(stepId); },
        onDrop: e => {
            e.preventDefault();
            if (dragId && dragOverId && dragId !== dragOverId) {
                moveStep(section.id, branchOf, dragId, dragOverId);
            }
            setDragId(null);
            setDragOverId(null);
        },
        onDragEnd: () => { setDragId(null); setDragOverId(null); },
    });

    const renderStep = (step, number, branchOf = null) => (
        <div key={step.id}>
            <StepRow
                step={step}
                number={number}
                lookups={{ ...lookups, sections: (playbook?.sections || []).filter(s => s.id !== section.id).map(s => ({ value: s.id, label: s.title })) }}
                slotOptions={slotOptions}
                expanded={selectedStepId === step.id}
                errors={errorsByStep[step.id] || []}
                onToggleMore={id => setSelectedStepId(prev => (prev === id ? null : id))}
                onUpdate={updateStepConfig}
                onChangeKind={updateStepKind}
                onRemove={removeStep}
                onCreateVariable={onCreateVariable}
                {...dropHandlers(branchOf, step.id)}
            />

            {/* Only the overflow a row cannot hold - button options, API headers
                and so on. The everyday fields are edited in the row itself. */}
            {selectedStepId === step.id && hasMore(step) && (
                <StepSettings step={step} lookups={lookups} slotOptions={slotOptions} />
            )}

            {step.kind === STEP_KIND.IF && (
                <div className="ml-12 border-l-2 border-purple-100 pl-3">
                    {(step.branches || []).map((branch, bIndex) => (
                        <div key={branch.id} className="py-1.5">
                            <div className="group/branch flex items-start gap-2.5 rounded-lg py-3 pl-1.5 pr-2 transition-colors hover:bg-gray-50/80">
                                <span className="mt-0.5 shrink-0 rounded-md bg-purple-50 px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-purple-600">
                                    {branchMode(branch) === 'else' ? 'else' : bIndex === 0 ? 'if' : 'else if'}
                                </span>
                                <div className="flex min-w-0 flex-1 items-start py-0.5">
                                    <BranchCondition branch={branch} slotOptions={slotOptions} onChange={patch => updateBranch(branch.id, patch)} />
                                </div>
                                <button
                                    type="button"
                                    onClick={() => removeBranch(branch.id)}
                                    className="rounded p-1 text-gray-300 hover:text-rose-600"
                                    aria-label="Delete branch"
                                >
                                    <HiOutlineTrash size={14} />
                                </button>
                            </div>

                            <div className="mt-1">
                                {(branch.steps || []).map((child, cIndex) =>
                                    renderStep(child, `${number}.${cIndex + 1}`, branch.id))}
                                <div className="pl-2">
                                    {/* no nested IF: keeps compiled conditions one level deep */}
                                    <AddStepMenu
                                        allowIf={false}
                                        label="Add step to branch"
                                        onAdd={kind => addStep(section.id, kind, branch.id)}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}

                    <div className="flex items-center gap-2"><button
                        type="button"
                        onClick={() => addBranch(step.id)}
                        className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-[13px] font-medium text-purple-600 transition-colors hover:bg-purple-50"
                    >
                        <HiOutlinePlus size={13} /> Add branch
                    </button>
                    {!(step.branches || []).some(branch => branchMode(branch) === 'else') && <button
                        type="button" onClick={() => addBranch(step.id, 'else')}
                        className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-[13px] font-medium text-gray-500 transition-colors hover:bg-gray-100">
                        <HiOutlinePlus size={13} /> Add ELSE
                    </button>}</div>
                </div>
            )}
        </div>
    );

    return (
        <section className="mb-4 rounded-xl border border-gray-200 bg-white px-3 py-4 shadow-sm sm:px-5">
            <div className="group/section mb-2.5 flex items-center gap-2">
                <span className="text-[17px] font-semibold tabular-nums text-gray-400">{index}.</span>
                {editingTitle ? (
                    <input
                        autoFocus
                        value={section.title}
                        onChange={e => renameSection(section.id, e.target.value)}
                        onBlur={() => setEditingTitle(false)}
                        onKeyDown={e => e.key === 'Enter' && setEditingTitle(false)}
                        className="min-w-0 flex-1 rounded-md border-gray-200 px-2 py-0.5 text-[17px] font-semibold text-gray-900 focus:border-gray-300 focus:ring-1 focus:ring-primary/20"
                    />
                ) : (
                    <button
                        type="button"
                        onClick={() => setEditingTitle(true)}
                        className="-mx-1 min-w-0 flex-1 truncate rounded px-1 text-left text-[17px] font-semibold tracking-tight text-gray-900 transition-colors hover:bg-gray-50"
                    >
                        {section.title}
                    </button>
                )}
                <button
                    type="button"
                    onClick={() => removeSection(section.id)}
                    className="rounded-md p-1 text-gray-300 opacity-0 transition hover:bg-rose-50 hover:text-rose-600 group-hover/section:opacity-100 focus:opacity-100"
                    aria-label="Delete section"
                >
                    <HiOutlineTrash size={15} />
                </button>
            </div>

            <div className="space-y-0.5">
                {(section.steps || []).map((step, i) => renderStep(step, `${index}.${i + 1}`))}
            </div>

            <div className="mt-4 border-t border-gray-100 pt-3 pl-2">
                <AddStepMenu onAdd={kind => addStep(section.id, kind)} />
            </div>
        </section>
    );
}
